import DropdownSelect from "../../../components/DropdownSelectModal";
import { lostReportStatusOptions, lostReportUrgentStatus, LostToFoundStatusMap, getAllowedLostStatusTargets } from "../utils/const";
import {
  ActionWrapper,
  CloseOutlinedIcon,
  DatePickerWrapper,
  EditIcon,
  FlexWrapper,
  HeaderWrapper,
  InfoWrapper,
  Label,
  LostInfoText,
  PenIconWrapper,
  ReportedInfoText,
  SelectWrapper,
  StyledHeading,
  StyledStatusWrapper,
  TrackingButton,
} from "./styles";
import { ReactComponent as FileIcon } from "../../../assets/images/FileIcon.svg";
import { PaymentStatus, Report } from "../../../types";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { fetchTicketsByStatus, updateLostItemDate, updateLostItemStatus } from "../../../redux/features/board/columnsAsyncThunk";
import { Button, DatePicker, Form, message, Modal, Select, Spin } from "antd";
import { convertIntoBoardTicket, updateTicketLocally, updateTicketStatusLocally } from "../../../redux/features/board/columnsSlice";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { convertUtcToLocal } from "../../../utils/helpers/convertUtcToLocal";
import { getPreMatchedFoundItemsAsyncThunk, sendPaymentRequest, updateLostItemCaseManager, updateLostItemIsUrgent } from "../../../redux/features/report/reportAsyncThunk";
import { columnConfig, columnOrderConfig } from "../../Reports/Board/const";
import { ReportType } from "../../../redux/features/board/columnTypes";
import DeleteReasonModal from "./DeleteReasonModal";
import { getAllCaseManager } from "../../../redux/features/user/userAsyncThunk";
import { selectUsers } from "../../../redux/features/user/userSlice";
import { clearReport, setPrematchModal, updateCaseManager, updateReportFoundItem, updateReportLFNotifications, updateReportStatus } from "../../../redux/features/report/reportSlice";
import ConfirmationModal from "../../../components/ConfirmationModal";
import { unAssignCaseMangerOptionValue } from "../../../utils/constants/filterOption";
import { RequestResults } from "../../../redux/common/RequestStatus";
import { SendOutlined, CheckCircleOutlined, CalendarFilled } from "@ant-design/icons";
import { LostStatuses, PendingRequestOperation } from "../../../types/LostStatuses";
import { selectUserRole } from "../../../redux/features/auth/selectors";
import { checkHasPermission } from "../../../utils/helpers/checkHasPermission";
import { permissions } from "../../../utils/constants/permissions";
import MarkAsShipped from "./MarkAsShippedModal";
import UnlinkAndUndoModal from "./UnlinkAndUndoModal";
import SystemPayRoutes from "../../../utils/constants/Routes";
import { updateFoundItemStatus } from "../../../redux/features/foundReport/foundReportAsyncThunk";
import { FoundStatuses } from "../../../types/FoundItemTypes";
import { FooterButtons } from "../../../components/ConfirmationModal/styles";
import { toast } from "react-toastify";
import { useQueryParams } from "../../../utils/hooks/useQueryParams";
import DeliveredModal from "../../../common/Delivered";
import { ShippingOption } from "../../../types/ShipTypes";
import LFNotifiedModal, { LFDepartmentDetails } from "./LFNotifiedModal";
import CopyToClipboard from "../../../components/CopyToClipboard";

interface ReportDetailsHeaderProps {
  report: Report | null;
  isLoading: boolean;
  versionData: Report | null;
}

const ReportDetailsHeader: React.FC<ReportDetailsHeaderProps> = ({ report, isLoading, versionData }) => {
  const { userAuthInfo } = useAppSelector((state) => state.auth);
  const [unlinkForm] = Form.useForm();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState<any>(report?.status);
  const [selectedStatusLabel, setSelectedStatusLabel] = useState<any>();

  // A claim is soft-deleted whenever `deletedAt` is set. Manual deletes ALSO flip status to Deleted (0),
  // but cron-triggered cleanups only set deletedAt and leave the workflow status intact (Open / NotPaid /
  // DraftIncomplete / etc.). Treating "soft-deleted" purely off `report.status === Deleted` misses the
  // cron case — and that's what causes "Status changed from Open to Open" when undoing a cron delete.
  const isSoftDeleted = !!report?.deletedAt;
  const displaySourceStatusLabel = useMemo(() => {
    if (isSoftDeleted) return lostReportStatusOptions.find(s => s.value === LostStatuses.Deleted)?.labelText || 'Deleted';
    return lostReportStatusOptions.find(s => s.value === selectedStatus)?.labelText || '';
  }, [isSoftDeleted, selectedStatus]);
  const [selectedCaseManager, setSelectedCaseManager] = useState<any>(report?.caseManager?.id);
  const [updateCaseManger, setUpdateCaseManger] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const [selectedDate, setSelectedDate] = useState(report?.lostDate ? dayjs(report.lostDate) : null);
  const [dateVisible, setDateVisible] = useState(false);
  const [dateUpdateLoading, setDateUpdateLoading] = useState(false);
  const [deliveredModalOpen, setDeliveredModalOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const [showEmailSendConfirm, setShowEmailSendConfirm] = useState(null);
  const columns = useAppSelector((state) => state.columns.tickets);
  const [showDeleteReasonModal, setShowDeleteReasonModal] = useState(false);

  const [shippedInfoPopup, setShippedInfoPopup] = useState<boolean>(false);
  const [sendPaymentReminderLoading, setSendPaymentReminderLoading] = useState(false);
  const { caseManagers, status } = useAppSelector(selectUsers);
  const userRole = useAppSelector(selectUserRole);
  const { data: paymentConfigurationData } = useAppSelector((state) => state.paymentConfiguration);

  const hasPermission = useCallback((permissionKey: string) => checkHasPermission(userRole, permissionKey), [userRole]);

  const isAllowMarkAsShipped = useMemo(() => hasPermission(permissions.AllowMarkAsShipped) && report?.trackingNumber && report?.shippingLabel?.id && !report?.isBillingInfoUpdated, [hasPermission, report]);
  const isFinalMatchStatusEnabled = useMemo(() => hasPermission(permissions.IsFinalMatchStatusEnabled), [hasPermission]);

  const [showUnlinkModal, setShowUnlinkModal] = useState(false);
  const [statusChangeInfo, setStatusChangeInfo] = useState<{
    currentStatus: string;
    newStatus: string;
    statusValue: any;
  } | null>(null);
  const [showLFNotifiedModal, setShowLFNotifiedModal] = useState(false);
  const [lfDepartmentDetails, setLfDepartmentDetails] = useState<LFDepartmentDetails | null>(null);
  const { setParam } = useQueryParams();

  const updateFoundItem = async (id: string, lostStatus: number, unlinkReason?: string, lostId?: string) => {
    const foundItemStatus = LostToFoundStatusMap[lostStatus] || FoundStatuses.Open;
    await dispatch(
      updateFoundItemStatus({
        id: id,
        status: foundItemStatus,
        unlinkReason,
        lostId
      })
    ).unwrap();
  };

  const handleUpdate = async (value: any, ...rest: any[]) => {
    // Check if status change is reverse order or requires special handling.
    // Soft-deleted rows (cron OR manual) are always considered "undo from deleted" when
    // the target is not Deleted — even if the underlying status was preserved by the cron.
    const isReverseStatusChange = value < selectedStatus;
    const isDeleteFlow = value === LostStatuses.Deleted;
    const isUndoFromDeleted = (selectedStatus === LostStatuses.Deleted || isSoftDeleted) && value !== LostStatuses.Deleted;
    const sendEmail = rest?.[0]?.sendEmail ?? true;
    const requiresModal = isReverseStatusChange || isUndoFromDeleted;

    if (isDeleteFlow) {
      setShowDeleteReasonModal(true);
      return;
    }
    // Conditions that require unlink modal
    if (requiresModal) {
      // For soft-deleted rows we want the unlink modal to read "from Deleted to <target>".
      // The form's `currentStatus` value is set to Deleted (0) so the disabled select shows
      // the right label and downstream `isDeleteUndoFlow` checks fire correctly.
      const currentStatus = isSoftDeleted
        ? lostReportStatusOptions.find((status) => status.value === LostStatuses.Deleted)
        : lostReportStatusOptions.find((status) => status.value === selectedStatus);
      const newStatus = lostReportStatusOptions.find((status) => status.value === value);

      setStatusChangeInfo({
        currentStatus: currentStatus?.labelText || "",
        newStatus: newStatus?.labelText || "",
        statusValue: value,
      });

      unlinkForm.setFieldsValue({
        currentStatus: currentStatus?.value ?? LostStatuses.Deleted,
        newStatus: newStatus?.value || "",
      });
      setShowUnlinkModal(true);
      return;
    }

    if (report?.id && !report?.pendingRequest?.id) {
      setLoading(true);
      try {
        const sourceStatus = report?.status;

        await dispatch(
          updateLostItemStatus({
            id: report?.id?.toString(),
            status: value,
            sendEmail
          })
        ).unwrap();
        await dispatch(updateReportStatus({ status: value }));
        const findUrgentItem = columns[LostStatuses.Urgent].find((item) => item?.id === report?.id);
        if (findUrgentItem?.id) {
          const response = await dispatch(updateLostItemIsUrgent({ id: findUrgentItem?.id, isUrgent: false })).unwrap();
          if (response) {
            await dispatch(updateTicketLocally({ ticket: convertIntoBoardTicket({ ...report, isUrgent: false }) }));
          }
        }

        if (report?.foundItem?.id) {
          await updateFoundItem(report?.foundItem?.id, value);
        }

        const targetLabel = lostReportStatusOptions.find(s => s.value === value)?.labelText || '';
        message.success({
          content: (
            <>
              Status changed from {displaySourceStatusLabel} to <b>{targetLabel}</b>
            </>
          ),
        });
        await dispatch(updateTicketStatusLocally({ ticket: convertIntoBoardTicket(report), sourceStatus, targetStatus: value }));
        setSelectedStatus(value);
        setShippedInfoPopup(false);
        setShowEmailSendConfirm(null);
      } catch (error) {
        console.log({ error });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpdateStatus = (value: any) => {
    // For soft-deleted rows (especially cron-deleted where status is preserved), the admin may
    // pick the SAME status as the current one to trigger recovery — we must not skip in that case.
    const isRecoveryClick = isSoftDeleted && value !== LostStatuses.Deleted;
    if (report?.id && !report?.pendingRequest?.id && (value !== selectedStatus || isRecoveryClick)) {
      // Check if this is a forward flow (increasing status number)
      const isForwardFlow = value > selectedStatus;
      const isDeleteFlow = value === LostStatuses.Deleted;
      const isUndoFromDeleted = (selectedStatus === LostStatuses.Deleted || isSoftDeleted) && value !== LostStatuses.Deleted;

      // If the claim is still inside the 24h refund window AND the admin is trying to
      // advance/process it (forward flow, non-delete), require an explicit confirmation —
      // customers may still refund. The confirmation modal will route to the correct next
      // step (LF Notified picker, email confirm, or direct update) once the admin proceeds.
      if (isWithinRefundWindow && isForwardFlow && !isDeleteFlow) {
        setRefundHoldConfirmTarget(value);
        return;
      }

      if (value === LostStatuses.LFNotified) {
        // Show LF Notified modal to collect department details
        setShowLFNotifiedModal(true);
        setStatusChangeInfo({
          currentStatus: selectedStatus,
          newStatus: 'LF Notified',
          statusValue: value
        });
      } else if (isForwardFlow && !isDeleteFlow && !isUndoFromDeleted) {
        // Show email confirmation modal for all forward flow changes (except delete and recovery).
        // Recovery from a soft-delete is conceptually an undo — go straight to the unlink/reason
        // modal without prompting for the customer email first.
        setShowEmailSendConfirm(value);
      } else {
        // Backward flow / delete / recovery from soft-delete — handle without email confirmation
        handleUpdate(value);
      }
    }
  };

  const handleLFNotifiedSave = async (detailsList: LFDepartmentDetails[], sendEmail: boolean = true) => {
    if (!report?.id || !statusChangeInfo || detailsList.length === 0) return;

    setLoading(true);
    try {
      const sourceStatus = report?.status;
      const targetStatus = statusChangeInfo.statusValue;

      // Call API with LF department details array — sendEmail reflects the modal's
      // "Do not notify customer via email" checkbox (default = no email).
      const statusUpdateResponse = await dispatch(
        updateLostItemStatus({
          id: report?.id?.toString(),
          status: targetStatus,
          sendEmail,
          lfDepartmentDetails: detailsList
        })
      ).unwrap();

      await dispatch(updateReportStatus({ status: targetStatus }));

      // Handle urgent item status if needed
      const findUrgentItem = columns[LostStatuses.Urgent]?.find((item) => item?.id === report?.id);
      if (findUrgentItem?.id) {
        const response = await dispatch(updateLostItemIsUrgent({ id: findUrgentItem?.id, isUrgent: false })).unwrap();
        if (response) {
          await dispatch(updateTicketLocally({ ticket: convertIntoBoardTicket({ ...report, isUrgent: false }) }));
        }
      }

      if (report?.foundItem?.id) {
        await updateFoundItem(report?.foundItem?.id, targetStatus);
      }

      // Show success message — when restoring from a soft-delete, label the source "Deleted"
      const isRecovering = isSoftDeleted && targetStatus !== LostStatuses.Deleted;
      const sourceStatusLabel = isRecovering
        ? (lostReportStatusOptions.find((status) => status.value === LostStatuses.Deleted)?.labelText || 'Deleted')
        : (lostReportStatusOptions.find((status) => status.value === sourceStatus)?.labelText || "");
      const targetStatusLabel = lostReportStatusOptions.find((status) => status.value === targetStatus)?.labelText || "";

      message.success({
        content: (
          <>
            Status changed from {sourceStatusLabel} to <b>{targetStatusLabel}</b>
            {detailsList.length > 1 && <> ({detailsList.length} departments notified)</>}
            {statusUpdateResponse?.emailSent && (
              <>
                <br />
                <span style={{ fontSize: '12px', color: '#52c41a' }}>
                  Email notification sent to customer
                </span>
              </>
            )}
          </>
        ),
        duration: statusUpdateResponse?.emailSent ? 5 : 3,
      });

      await dispatch(updateTicketStatusLocally({ ticket: convertIntoBoardTicket(report), sourceStatus, targetStatus }));
      setSelectedStatus(targetStatus);
      setShowLFNotifiedModal(false);
      setStatusChangeInfo(null);
      setLfDepartmentDetails(detailsList[0]); // Store first for display

      // Update report in Redux with LF notification data so body renders without refresh
      const firstEntry = detailsList[0];
      dispatch(updateReportLFNotifications({
        lfDepartmentName: firstEntry.lfDepartmentName,
        lfContactPerson: firstEntry.lfContactPerson,
        lfContactEmail: firstEntry.lfContactEmail,
        lfContactPhone: firstEntry.lfContactPhone,
        lfNotes: firstEntry.lfNotes,
        additionalComments: firstEntry.additionalComments,
        establishmentName: firstEntry.establishmentName,
        lfNotifiedAt: new Date().toISOString(),
        lfNotifications: detailsList,
      }));
    } catch (error) {
      console.error('Error updating status to LF Notified:', error);
      message.error('Failed to update status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCaseManager = async (value: any) => {
    if (report?.id) {
      try {
        setLoading(true);
        const isUnAssigned = value === unAssignCaseMangerOptionValue;
        await dispatch(
          updateLostItemCaseManager({
            id: report?.id?.toString(),
            caseManager: isUnAssigned ? null : value,
          })
        ).unwrap();
        const oldCaseManagerName = report?.caseManager?.id
          ? caseManagers?.find((user) => user?.id === report?.caseManager?.id)?.firstName + " " + caseManagers?.find((user) => user?.id === report?.caseManager?.id)?.lastName
          : "";
        const newCaseManagerName = isUnAssigned ? "" : caseManagers?.find((user) => user?.id === value)?.firstName + " " + caseManagers?.find((user) => user?.id === value)?.lastName;
        const newCaseManagerFirstName = isUnAssigned ? "" : caseManagers?.find((user) => user?.id === value)?.firstName;
        const newCaseManger = isUnAssigned ? undefined : caseManagers?.find((user) => user?.id === value);
        message.success({
          content: (
            <>
              {isUnAssigned ? (
                <>Case Manager {oldCaseManagerName} unassigned</>
              ) : oldCaseManagerName ? (
                <>
                  Case Manager changed from {oldCaseManagerName} to <strong>{newCaseManagerName}</strong>
                </>
              ) : (
                <>
                  Report assigned to <strong>{newCaseManagerName}</strong>
                </>
              )}
            </>
          ),
        });
        await dispatch(updateTicketLocally({ ticket: convertIntoBoardTicket({ ...report, caseManagerName: newCaseManagerFirstName, caseManager: newCaseManger }) }));
        await dispatch(updateCaseManager({ caseManager: newCaseManger }));
        setSelectedCaseManager(isUnAssigned ? "" : value);
        setUpdateCaseManger("");
      } catch (error) {
        console.log({ error });
      } finally {
        setLoading(false);
      }
    }
  };

  // Dispatch API calls for each status in columnConfig
  const fetchAllTickets = async () => {
    await Promise.all(columnConfig.map(({ status, type }) => {
      const columnType = type ?? ReportType.Lost
      const order = columnOrderConfig[`${status}-${columnType}`];
      return dispatch(fetchTicketsByStatus({ status, offset: 0, limit: 50, filters: {}, type: columnType, order: order }))
    }));
  };

  useEffect(() => {
    if (Object.keys(columns).length === 0 && report?.id) {
      fetchAllTickets();
    }
  }, [columns, report?.id]);


  useEffect(() => {
    if (report?.lostDate) {
      setSelectedDate(dayjs(report.lostDate));
    }
  }, [report]);

  // useEffect(() => {
  //   if (report?.id && report?.status > LostStatuses.Open && report?.status !== LostStatuses.Delete) {
  //     dispatch(getPreMatchedFoundItemsAsyncThunk({ targetDate: "", trackingId: report?.id }));
  //   }
  // }, [report]);

  const handleIconClick = () => {
    setDateVisible(!dateVisible);
  };

  // Handle outside click to close DatePicker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click is outside the DatePicker wrapper AND not on the DatePicker popup
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node) && dateVisible) {
        // Check if the click is on the DatePicker popup (which has class 'ant-picker-dropdown' or 'lost-date-picker')
        const target = event.target as HTMLElement;
        const isDatePickerPopup = target.closest('.ant-picker-dropdown') || target.closest('.lost-date-picker');

        // Only close if the click is not on the DatePicker popup
        if (!isDatePickerPopup) {
          setDateVisible(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dateVisible]);

  const handleDateChange = async (date: any) => {
    try {
      if (date && report?.id) {
        const lostDate = date?.toISOString();
        setDateUpdateLoading(true);
        setSelectedDate(date);
        setDateVisible(false);
        await dispatch(
          updateLostItemDate({
            id: report?.id?.toString(),
            lostDate: lostDate,
          })
        ).unwrap();
        await dispatch(updateTicketLocally({ ticket: convertIntoBoardTicket({ ...report, lostDate: lostDate }) }));
      }
    } catch (error) {
    } finally {
      setDateUpdateLoading(false);
      setDateVisible(false);
    }
  };

    // Status the claim held immediately before deletion (from the backend's Delete UndoReason).
    // Used only to decide paid-vs-unpaid when there's no payment record to read.
    const deletedFromStatus: number | null = (report as any)?.deletedFromStatus ?? null;
    const PAID_WORKFLOW = [LostStatuses.Open, LostStatuses.Pending, LostStatuses.Review, LostStatuses.InProcess, LostStatuses.LFNotified, LostStatuses.Overdue];
    // Whether the claim actually has a successful payment — determined from the payment list,
    // NOT from the current status (which is "Deleted" and would always read as unpaid).
    const reportPayments = report?.payment;
    const claimHasSucceededPayment: boolean = Array.isArray(reportPayments)
      ? reportPayments.some((p: any) => p?.status === PaymentStatus.Succeeded)
      : false;

  // All statuses a deleted claim may be restored to — must mirror the backend's allowed
  // targets in handleUndoFlow (lost.controller.ts). The admin can pick ANY of these, not
  // just the single pre-deletion status. Paid claims restore into the live workflow; unpaid
  // claims restore into their draft/unpaid lifecycle.
  // Pending and In Process are deprecated/grandfathered statuses — excluded from restore
  // targets so admins don't move claims back into states we no longer use in the workflow.
  const PAID_RESTORE_TARGETS = [LostStatuses.Open, LostStatuses.Review, LostStatuses.LFNotified, LostStatuses.Overdue];
  const UNPAID_RESTORE_TARGETS = [LostStatuses.NotPaid, LostStatuses.DraftIncomplete, LostStatuses.DraftCompleted];
  const resolveRecoveryTargets = (currentStatus: number, claimIsPaid: boolean): number[] => {
    const treatAsPaid = claimIsPaid
      || PAID_WORKFLOW.includes(currentStatus)
      || (deletedFromStatus != null && PAID_WORKFLOW.includes(deletedFromStatus));
    return treatAsPaid ? PAID_RESTORE_TARGETS : UNPAID_RESTORE_TARGETS;
  };

  const statusOptionsWithDisabled = useMemo(() => {
    const currentStatus = report?.status ?? LostStatuses.Open;
    const allowedStatuses = getAllowedLostStatusTargets(currentStatus);

    const recoveryTargets = isSoftDeleted
      ? resolveRecoveryTargets(currentStatus, claimHasSucceededPayment)
      : [];

    return lostReportStatusOptions.map((status) => {
      const isCurrentStatus = status.value === currentStatus;
      const isRecoveryTarget = recoveryTargets.includes(status.value);
      const isAllowed = isCurrentStatus || allowedStatuses.includes(status.value) || isRecoveryTarget;

      return {
        ...status,
        disabled: !isAllowed,
      };
    });
  }, [report?.status, isSoftDeleted, deletedFromStatus, claimHasSucceededPayment]);

  const filteredStatusOptions = useMemo(() => {
    const currentStatus = report?.status ?? LostStatuses.Open;
    const paidStatuses = [LostStatuses.Open, LostStatuses.Pending, LostStatuses.Review, LostStatuses.InProcess, LostStatuses.LFNotified, LostStatuses.Overdue];
    const isPaid = paidStatuses.includes(currentStatus);

    // For any soft-deleted row, surface ALL valid restore targets so the admin can move the
    // claim back to whichever status they want (not just its pre-deletion status), plus keep
    // Delete visible. Without this, deleted claims only show "Deleted".
    if (isSoftDeleted) {
      const recoveryTargets = resolveRecoveryTargets(currentStatus, claimHasSucceededPayment);
      const visibleSet = new Set([currentStatus, ...recoveryTargets, LostStatuses.Deleted]);
      return statusOptionsWithDisabled.filter((opt) => visibleSet.has(opt.value));
    }

    if (isPaid) {
      // Paid claims always show Open, Review, LF Notified, Delete (disabled based on transition map)
      const paidVisibleSet = new Set([LostStatuses.Open, LostStatuses.Review, LostStatuses.LFNotified, LostStatuses.Deleted]);
      return statusOptionsWithDisabled.filter((opt) => paidVisibleSet.has(opt.value));
    }

    // Non-paid: only show current status + allowed transitions
    const allowedTargets = getAllowedLostStatusTargets(currentStatus);
    const visibleSet = new Set([currentStatus, ...allowedTargets]);
    return statusOptionsWithDisabled.filter((opt) => visibleSet.has(opt.value));
  }, [statusOptionsWithDisabled, report?.status, isSoftDeleted, deletedFromStatus, claimHasSucceededPayment]);

  const isShippingAddressUpdateRequest = useMemo(() => {
    return report?.pendingRequest?.operation === PendingRequestOperation.UpdateShippingInfo
  }, [report?.pendingRequest])

  const isMarkAsDelivered = useMemo(() => {
    return report?.deliveryDate;
  }, [report?.deliveryDate])

  useEffect(() => {
    if (report?.status != null) {
      const findOptionFromFilter = filteredStatusOptions.find((option) => option.value === report?.status);
      if (!findOptionFromFilter) {
        const findOptionFromStatus = lostReportStatusOptions.find((option) => option.value === report?.status);
        setSelectedStatusLabel(findOptionFromStatus?.label);
      } else {
        setSelectedStatusLabel(null);
      }
    }
  }, [report?.status, lostReportStatusOptions, filteredStatusOptions]);

  useEffect(() => {
    if (report?.status != null) {
      setSelectedStatus(report?.status);
    }
  }, [report?.status]);

  useEffect(() => {
    dispatch(getAllCaseManager());
  }, [dispatch]);

  useEffect(() => {
    if (report?.caseManager?.id) {
      setSelectedCaseManager(report?.caseManager?.id);
    } else {
      setSelectedCaseManager("");
    }
  }, [report?.caseManager?.id]);

  const caseManagerUsers = useMemo(() => {
    if (caseManagers?.length > 0) {
      const users: { value: string; label: ReactNode; key: string }[] = caseManagers?.map((user) => ({
        value: user?.id,
        label: user?.firstName + " " + user?.lastName,
        key: user?.id,
      }));

      if (report?.caseManager?.id) {
        // Add "Unassign User" option at the end
        users.push({
          value: unAssignCaseMangerOptionValue,
          label: <span style={{ color: "red" }}>Unassign User</span>,
          key: unAssignCaseMangerOptionValue,
        });
      }

      return users;
    }
    return [];
  }, [caseManagers, report?.caseManager?.id]);

  const confirmUpdateCaseManager = useMemo(() => {
    if (caseManagerUsers?.length > 0) {
      const currentCaseManager = caseManagerUsers?.find((user) => user?.value === selectedCaseManager)?.label;
      const newCaseManager = caseManagerUsers?.find((user) => user?.value === updateCaseManger)?.label;
      return updateCaseManger && updateCaseManger !== unAssignCaseMangerOptionValue && currentCaseManager
        ? `Are you sure to unassign ${currentCaseManager} and assign to ${newCaseManager} for this report?`
        : updateCaseManger && updateCaseManger === unAssignCaseMangerOptionValue && currentCaseManager
          ? `Are you sure to unassign ${currentCaseManager}?`
          : updateCaseManger && !selectedCaseManager
            ? `Are you sure to assign ${newCaseManager} for this report?`
            : "";
    }
    return "";
  }, [selectedCaseManager, updateCaseManger, caseManagerUsers]);

  const confirmUpdateCaseManagerTitle = useMemo(() => {
    if (caseManagerUsers?.length > 0) {
      return updateCaseManger === unAssignCaseMangerOptionValue ? "Confirm Unassigning?" : "Confirm Assigning?";
    }
    return "";
  }, [selectedCaseManager, updateCaseManger, caseManagerUsers]);

  const handleUnlinkAndUpdate = async (sendEmail = true) => {
    if (!report?.id) return;

    let formValues;
    try {
      formValues = await unlinkForm.validateFields();
      setLoading(true);
    } catch (error) {
      return;
    }
    const { unlinkReason, reason, recoverReason } = formValues;
    // Any soft-deleted row needs withDeleted so the backend can resolve it. Cron-deleted rows
    // have status preserved (not Deleted), so checking status alone misses them.
    const withDeleted = report?.status === LostStatuses.Deleted || isSoftDeleted;
    try {
      if (!statusChangeInfo) return;

      const sourceStatus = report?.status;
      const targetStatus = statusChangeInfo.statusValue;

      // Determine if we need to include reason and/or unlinkReason based on the flow type
      const isReverseFlow = targetStatus < sourceStatus && targetStatus !== LostStatuses.Deleted;
      const isDeleteFlow = targetStatus === LostStatuses.Deleted;
      // Recovery flow: any soft-deleted row being restored to anything other than Deleted.
      const isDeleteUndoFlow = (sourceStatus === LostStatuses.Deleted || isSoftDeleted) && targetStatus !== LostStatuses.Deleted;

      // Prepare the API request parameters
      const requestParams = {
        id: report?.id?.toString(),
        status: targetStatus,
        reason: recoverReason ? recoverReason : formValues?.discardReason?.toLocaleLowerCase() === "other" ? formValues?.discardReasonText : formValues?.discardReason,
        unlinkReason: undefined,
        sendEmail,
        withDeleted
      };

      // Call the API with the appropriate parameters
      const statusUpdateResponse = await dispatch(updateLostItemStatus(requestParams)).unwrap();

      // Update local state
      await dispatch(updateReportStatus({ status: targetStatus }));

      // Handle urgent item status if needed (when moving to Shipped)
      const findUrgentItem = columns[LostStatuses.Urgent]?.find((item) => item?.id === report?.id);
      if (findUrgentItem?.id) {
        const response = await dispatch(updateLostItemIsUrgent({ id: findUrgentItem?.id, isUrgent: false })).unwrap();
        if (response) {
          await dispatch(updateTicketLocally({ ticket: convertIntoBoardTicket({ ...report, isUrgent: false }) }));
        }
      }
      if (report?.foundItem?.id) {
        await updateFoundItem(report?.foundItem?.id, targetStatus, unlinkReason, report?.id);
      }
      if (targetStatus === LostStatuses.InProcess && report?.foundItem) {
        dispatch(updateReportFoundItem({ foundItem: null }))
      }
      if (isDeleteUndoFlow) {
        setParam("withDeleted", "false")
      }

      // // Show success message — when restoring from a soft-delete, label the source "Deleted"
      // regardless of the row's underlying status (cron-deleted items preserve their status).
      const sourceStatusLabel = isDeleteUndoFlow
        ? (lostReportStatusOptions.find((status) => status.value === LostStatuses.Deleted)?.labelText || 'Deleted')
        : (lostReportStatusOptions.find((status) => status.value === sourceStatus)?.labelText || "");
      const targetStatusLabel = lostReportStatusOptions.find((status) => status.value === targetStatus)?.labelText || "";

      message.success({
        content: (
          <>
            Status changed from {sourceStatusLabel} to <b>{targetStatusLabel}</b>
            {statusUpdateResponse?.emailSent && (
              <>
                <br />
                <span style={{ fontSize: '12px', color: '#52c41a' }}>
                  📧 Email notification sent to customer
                </span>
              </>
            )}
          </>
        ),
        duration: statusUpdateResponse?.emailSent ? 5 : 3,
      });

      // Update ticket status locally
      await dispatch(
        updateTicketStatusLocally({
          ticket: convertIntoBoardTicket(report),
          sourceStatus,
          targetStatus,
        })
      );

      // Update selected status
      setSelectedStatus(targetStatus);
      setShowUnlinkModal(false);
      setStatusChangeInfo(null);
      setShippedInfoPopup(false);
      unlinkForm.resetFields();
      if (targetStatus === LostStatuses.Deleted) {
        await dispatch(clearReport());
        navigate(SystemPayRoutes.reports);
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPaymentReminder = async () => {
    if (report?.id && report?.foundItem?.id) {
      try {
        setSendPaymentReminderLoading(true);
        // Get selected shipping option IDs
        const selectedShippingOptionIds = report?.shippingOptions
          ?.filter((option: ShippingOption) => option.isSelected && option.totalAmount)
          ?.map((option: ShippingOption) => option.id.toString()) || [];
        const requestPayload = {
          lost: report?.id ?? "",
          found: report?.foundItem?.id ?? "",
          selected: selectedShippingOptionIds
        }
        await dispatch(sendPaymentRequest(requestPayload)).unwrap();
        toast.success("Payment reminder sent successfully");
      } catch (error) {
      } finally {
        setSendPaymentReminderLoading(false);
      }
    }
  }

  const paymentList = report?.payment;
  const isPaymentInfoReady = Array.isArray(paymentList);

  const isPaymentSucceeded = useMemo(() => {
    if (!isPaymentInfoReady || !paymentList) return false;
    return paymentList.some((payment) => payment.status === PaymentStatus.Succeeded);
  }, [isPaymentInfoReady, paymentList]);

  // Refund-window detection (24h FIXED business rule, independent of the visual highlight
  // setting). When still inside the window the customer may refund, so we surface a confirm
  // dialog before progressing the claim into LF Notified — admins should normally hold off.
  const succeededPaymentCreatedAt = useMemo(() => {
    if (!isPaymentInfoReady || !paymentList) return null;
    const succeeded = paymentList.find((p) => p.status === PaymentStatus.Succeeded);
    return succeeded?.createdAt || null;
  }, [isPaymentInfoReady, paymentList]);
  const REFUND_WINDOW_HOURS = 24;
  const refundHoldRemaining = useMemo(() => {
    if (!succeededPaymentCreatedAt) return null;
    const remainingMs = REFUND_WINDOW_HOURS * 60 * 60 * 1000 - (Date.now() - new Date(succeededPaymentCreatedAt).getTime());
    if (remainingMs <= 0) return null;
    const totalMinutes = Math.floor(remainingMs / 60000);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }, [succeededPaymentCreatedAt]);
  const isWithinRefundWindow = !!refundHoldRemaining;

  // Holds the target status while the refund-hold confirmation modal is open. Null = no
  // confirmation pending. Set when the admin picks a forward status while still in the window.
  const [refundHoldConfirmTarget, setRefundHoldConfirmTarget] = useState<number | null>(null);

  const isPayAfterCutOffDateOverdue = useMemo(() => {
    if (!paymentConfigurationData || !report?.shippingOptions?.length || !isPaymentInfoReady || isPaymentSucceeded) return false;
    const { paymentDueDays } = paymentConfigurationData;
    return report?.shippingOptions?.length > 0 && report?.shippingOptions.every(item => {
      const deadline = dayjs(item.cutOffDate).add(paymentDueDays, "day");
      return dayjs().isAfter(deadline);
    });
  }, [report?.shippingOptions, paymentConfigurationData, report, isPaymentInfoReady, isPaymentSucceeded]);

  return (
    <>
      <HeaderWrapper>
        <FlexWrapper>
          <FileIcon />
          <CopyToClipboard text={report?.trackingId}>
            <StyledHeading>{report?.trackingId}</StyledHeading>
          </CopyToClipboard>
          <InfoWrapper>
            <LostInfoText>
              <span>Lost: </span>
              <span>{selectedDate ? convertUtcToLocal(selectedDate?.toString(), "MM/DD/YYYY") : ""}</span>
            </LostInfoText>
            {report && (
              <PenIconWrapper onClick={handleIconClick}>
                <EditIcon />
              </PenIconWrapper>
            )}
            <Spin spinning={dateUpdateLoading}>
              <DatePickerWrapper ref={datePickerRef}>
                {dateVisible && (
                  <DatePicker
                    // minDate={report?.departureDate ? dayjs(report?.departureDate) : undefined}
                    // maxDate={report?.debarkDate ? dayjs(report?.debarkDate) : undefined}
                    open={dateVisible}
                    value={selectedDate}
                    onChange={handleDateChange}
                    popupClassName="lost-date-picker"
                  />
                )}
              </DatePickerWrapper>
            </Spin>
            <ReportedInfoText>
              <span>Reported: </span>
              <span>
                {report?.createdAt && convertUtcToLocal(report?.createdAt, "MM/DD/YYYY")} @ {report?.createdAt && convertUtcToLocal(report?.createdAt, "HH:mm:ss")}
              </span>
            </ReportedInfoText>
            {succeededPaymentCreatedAt && (
              <>
                <span style={{ color: "#d9d9d9", margin: "0 8px" }}>|</span>
                <ReportedInfoText>
                  <span>Paid: </span>
                  <span>
                    {convertUtcToLocal(succeededPaymentCreatedAt, "MM/DD/YYYY")} @ {convertUtcToLocal(succeededPaymentCreatedAt, "HH:mm:ss")}
                  </span>
                </ReportedInfoText>
              </>
            )}
          </InfoWrapper>
        </FlexWrapper>
        <ActionWrapper>
          <SelectWrapper>
            {isAllowMarkAsShipped && (
              <TrackingButton onClick={() => setShippedInfoPopup(true)} type="primary" icon={<CheckCircleOutlined style={{ fontSize: 20, color: "#fff" }} />}>
                Mark as Shipped
              </TrackingButton>
            )}
            {isFinalMatchStatusEnabled && report?.isBillingInfoUpdated && !isShippingAddressUpdateRequest && (
              <TrackingButton
                onClick={() => {
                  dispatch(setPrematchModal({ isOpen: true }));
                }}
                type="primary"
                icon={<SendOutlined />}
              >
                Resend Quote
              </TrackingButton>
            )}

            {isFinalMatchStatusEnabled && isPaymentInfoReady && !report?.isBillingInfoUpdated && !isShippingAddressUpdateRequest && !isPayAfterCutOffDateOverdue && !isPaymentSucceeded && (
              <TrackingButton
                onClick={() => handleSendPaymentReminder()}
                type="primary"
                icon={<SendOutlined />}
                loading={sendPaymentReminderLoading}
                disabled={sendPaymentReminderLoading}
              >
                Send Payment Reminder
              </TrackingButton>
            )}

            {isFinalMatchStatusEnabled && isPaymentInfoReady && !report?.isBillingInfoUpdated && !isShippingAddressUpdateRequest && isPayAfterCutOffDateOverdue && (
              <TrackingButton
                onClick={() => dispatch(setPrematchModal({ isOpen: true }))}
                type="primary"
                icon={<CalendarFilled />}
                loading={sendPaymentReminderLoading}
                disabled={sendPaymentReminderLoading}
              >
                Set New Pay-by Date
              </TrackingButton>
            )}
            {report?.status !== LostStatuses.Deleted && <StyledStatusWrapper>
              <Label>Case Manager</Label>
              <Select
                style={{ minWidth: 150 }}
                placeholder="Select Case Manager"
                value={selectedCaseManager ? selectedCaseManager : undefined}
                options={caseManagerUsers}
                loading={loading || status === RequestResults.pending}
                onSelect={(value: any) => setUpdateCaseManger(value)}
              />
            </StyledStatusWrapper>}
            <StyledStatusWrapper>
              <Label>Status</Label>
              <Select style={{ minWidth: 150 }} value={selectedStatusLabel ?? selectedStatus} options={filteredStatusOptions} loading={loading} onSelect={handleUpdateStatus} />
              <CloseOutlinedIcon onClick={() => navigate(-1)} />
            </StyledStatusWrapper>
          </SelectWrapper>
        </ActionWrapper>
        {showDeleteReasonModal && <DeleteReasonModal isOpen={showDeleteReasonModal} onClose={() => setShowDeleteReasonModal(false)} selectedRecord={report} />}
        {/* {isModalOpen && <TrackingModal isModalOpen={isModalOpen} onCancel={() => setIsModalOpen(false)} />} */}
        {updateCaseManger && (
          <ConfirmationModal
            isModalOpen={updateCaseManger ? true : false}
            onCancel={() => setUpdateCaseManger("")}
            message={confirmUpdateCaseManager}
            title={confirmUpdateCaseManagerTitle}
            loading={loading}
            handleSave={() => handleUpdateCaseManager(updateCaseManger)}
            CancelButtonText="Cancel"
            confirmButtonText={updateCaseManger === unAssignCaseMangerOptionValue ? "Unassign" : "Assign"}
          />
        )}
        {showEmailSendConfirm && (
          <ConfirmationModal
            isModalOpen={showEmailSendConfirm}
            onCancel={() => setShowEmailSendConfirm(null)}
            message={`Do you want to send the guest an ${lostReportStatusOptions.find((status) => status.value === showEmailSendConfirm)?.labelText} Email?`}
            title={"Send Email"}
            loading={loading}
            handleSave={() => handleUpdate(showEmailSendConfirm, { sendEmail: true })}
            CancelButtonText="No"
            confirmButtonText="Yes"
            footerButton={
              <FooterButtons>
                <Button onClick={() => handleUpdate(showEmailSendConfirm, { sendEmail: false })} disabled={loading}>{"No"}</Button>
                <Button type="primary" onClick={() => handleUpdate(showEmailSendConfirm, { sendEmail: true })} loading={loading}>
                  {"Yes"}
                </Button>
              </FooterButtons>
            }
          />
        )}
        {showUnlinkModal && (
          <UnlinkAndUndoModal
            isOpen={showUnlinkModal}
            onClose={() => {
              setShowUnlinkModal(false);
              setStatusChangeInfo(null);
              unlinkForm.resetFields();
            }}
            onSave={handleUnlinkAndUpdate}
            loading={loading}
            form={unlinkForm}
          />
        )}
        {deliveredModalOpen && (
          <DeliveredModal
            isDeliveredModalOpen={deliveredModalOpen}
            handleDeliveredCancel={() => setDeliveredModalOpen(false)}
            handleDeliveredSuccess={() => setDeliveredModalOpen(false)}
          />
        )}
        {showLFNotifiedModal && (
          <LFNotifiedModal
            isOpen={showLFNotifiedModal}
            onClose={() => {
              setShowLFNotifiedModal(false);
              setStatusChangeInfo(null);
            }}
            onSave={handleLFNotifiedSave}
            loading={loading}
            report={report}
          />
        )}

        {/* Refund-hold warning: shown when the admin tries to advance a claim while the
            customer is still inside the refund window. Clear messaging + remaining time so
            staff knows to wait. On confirm we resume the original flow (LF Notified picker
            for LFNotified, email-confirm for other forward statuses). */}
        <Modal
          title={<span style={{ color: "#ff4d4f", fontWeight: 600 }}>⚠ Customer can still request a refund</span>}
          open={refundHoldConfirmTarget !== null}
          onCancel={() => setRefundHoldConfirmTarget(null)}
          okText="Continue anyway"
          okButtonProps={{ danger: true }}
          cancelText="Wait — don't process yet"
          onOk={() => {
            const target = refundHoldConfirmTarget;
            setRefundHoldConfirmTarget(null);
            if (target === null) return;
            // Resume the flow the same way handleUpdateStatus would have, now that the admin
            // has explicitly acknowledged the refund window.
            if (target === LostStatuses.LFNotified) {
              setShowLFNotifiedModal(true);
              setStatusChangeInfo({
                currentStatus: selectedStatus,
                newStatus: "LF Notified",
                statusValue: target,
              });
            } else if (target === LostStatuses.Deleted) {
              setShowDeleteReasonModal(true);
            } else if (target > selectedStatus) {
              // Other forward flow — go through email-confirm as usual.
              setShowEmailSendConfirm(target as any);
            } else {
              handleUpdate(target);
            }
          }}
          width={520}
        >
          <div style={{ fontSize: 14, lineHeight: 1.6 }}>
            <p style={{ marginTop: 0 }}>
              This claim was paid on{" "}
              <strong>
                {succeededPaymentCreatedAt
                  ? `${new Date(succeededPaymentCreatedAt).toLocaleString()}`
                  : "the recent past"}
              </strong>
              .
            </p>
            <p>
              The customer is <strong>still eligible to request a refund</strong> for the next{" "}
              <strong style={{ color: "#ff4d4f" }}>{refundHoldRemaining}</strong>.
            </p>
            <p style={{ marginBottom: 0 }}>
              We recommend waiting until the {REFUND_WINDOW_HOURS}-hour refund window closes
              before progressing this claim. If you continue and the customer refunds, your
              work may need to be undone.
            </p>
          </div>
        </Modal>
      </HeaderWrapper>
    </>
  );
};

export default ReportDetailsHeader;
